import * as React from "react";
import { ComboBox, PrimaryButton, IComboBoxOption, ISelectableOption, ActionButton, SelectableOptionMenuItemType } from "office-ui-fabric-react";
import { connect } from "react-redux";
import SmartsheetConnectionSelect from "./SmartsheetConnectionSelect";
import { Dictionary, ISourceInfo } from "../../../entities/common";
import OverlayComponent from "../../common/OverlayComponent";
import { ApplicationState } from "../../../store/index";
import { actionCreators, ISmartsheetSheet, ISmartsheetProjectSourceData, ISmartsheetConnectionState, ISmartsheetWorkspaceInfo } from "../../../store/integration/SmartsheetStore";
import SmartsheetMappingPanel from "./SmartsheetMappingPanel";
import { ConnectionField } from "../ConnectionField";
import { IListStore, validateConnection } from "../../../store/integration/common";
import { usePrevious } from "../../utils/effects";
import SwitchPrimaryScheduleConfirmation from "../SwitchPrimaryScheduleConfirmation";
import { SourceType } from "../../../store/ExternalEpmConnectStore";
import { FieldValue } from "../ConnectionFieldValue";

export type SmartsheetConnectActions = {
    deleteLink: (connectionId: string) => void,
    linkToSmartsheetSheet: (obj: { connectionId: string, data: ISmartsheetProjectSourceData, setAsPrimary?: boolean }) => void
}

type OwnProps = {
    readonly: boolean;
    actions: SmartsheetConnectActions;
    allowMultipleConnectionsToOneProject?: boolean;
    disableConfigureConnection?: boolean;
    expandAdditionalFilter?: boolean;
    sourceInfo?: ISourceInfo<ISmartsheetProjectSourceData>;
    dismissPanel?: () => void;
    showSwitchPrimaryDialog?: boolean;
    setAsPrimary?: boolean;
}

type StateProps = {
    connections: ISmartsheetConnectionState;
    workspacesInfo: IListStore<ISmartsheetWorkspaceInfo>;
    sheetsInfo: IListStore<ISmartsheetSheet>;
    sheetsByWorkspaceMap: Dictionary<ISmartsheetSheet[]>;
};

type Props = OwnProps & StateProps & typeof actionCreators;

const allWorkspacesKey = -2;
const sheetsMapToOptions = (map: Dictionary<ISmartsheetSheet[]>): Dictionary<ISelectableOption[]> =>
    Object.keys(map).reduce<Dictionary<ISelectableOption[]>>((acc, key) => {
        acc[key] = sheetsToOptions(map[key]);
        return acc;
    }, {});

const sheetsToOptions = (sheets: ISmartsheetSheet[]): ISelectableOption[] =>
    sheets.map(_ => ({ key: _.sheetId, text: _.sheetName, disabled: _.isAlreadyLinked }))
        .sort((a, b) => a.text.localeCompare(b.text));

const workspacesToOptions = (infos  : ISmartsheetWorkspaceInfo[]): ISelectableOption[] =>
    [
        { key: allWorkspacesKey, text: 'All Workspaces' },
        { key: "div", itemType: SelectableOptionMenuItemType.Header, text: "Workspaces" },
        ...infos
            .map(_ => ({ key: _.workspaceId, text: _.workspaceName }))
            .sort((a, b) => a.text.localeCompare(b.text))
    ];

const onRenderItem = (props?: IComboBoxOption, defaultRender?: (props?: IComboBoxOption) => JSX.Element | null): JSX.Element | null => {
    if (!defaultRender) {
        return null;
    }

    const element = defaultRender(props);
    if (!props?.disabled) {
        return element;
    }
    return React.createElement("div", { title: "Already linked" }, element);
}

const linkToProject = (props: Props, connectionId: string, workspaceId: number, sheetId: number, setAsPrimary?: boolean) => {
    const { sheetsByWorkspaceMap, actions, dismissPanel, sheetsInfo } = props;

    const itemsSheet = workspaceId === allWorkspacesKey
        ? sheetsInfo.entities.find(_ => _.sheetId === sheetId)
        : sheetsByWorkspaceMap[workspaceId].find(_ => _.sheetId === sheetId);

    if (!itemsSheet) {
        return;
    }

    actions.linkToSmartsheetSheet({
        connectionId,
        data: {
            workspaceId: itemsSheet.workspaceId,
            workspaceName: itemsSheet.workspaceName,
            sheetId: itemsSheet.sheetId,
            sheetName: itemsSheet.sheetName,
            sheetUrl: itemsSheet.sheetUrl,
        },
        setAsPrimary: !!setAsPrimary
    });
    dismissPanel?.();
}

const deleteLink = (props: Props, connectionId: string) => {
    const { actions, dismissPanel } = props;

    actions.deleteLink(connectionId);
    dismissPanel?.();
}

const SmartsheetConnectControl = (props: Props) => {
    const { readonly, sheetsByWorkspaceMap, workspacesInfo, sheetsInfo, sourceInfo, allowMultipleConnectionsToOneProject, disableConfigureConnection,
        connections, showSwitchPrimaryDialog } = props;
    const [connectionId, setConnectionId] = React.useState<string | undefined>(sourceInfo?.connectionId);
    const [sheetId, setsheetId] = React.useState<number | undefined>(sourceInfo?.sourceData.sheetId);
    const [workspaceId, setWorkspaceId] = React.useState<number | undefined>(sourceInfo?.sourceData.workspaceId);
    const [isConfigureConnection, setIsConfigureConnection] = React.useState<boolean | undefined>();
    const [showSwitchPrimaryScheduleConfirm, setShowSwitchPrimaryScheduleConfirm] = React.useState<boolean>(false);

    const isLinked = !!sourceInfo;
    const previousConnectionsVerification = usePrevious(props.connections.connectionsVerification);

    React.useEffect(() => {
        const validationInfo = validateConnection(connections, connectionId);
        if (!isLinked && !!connectionId && previousConnectionsVerification?.[connectionId]?.isValidating !== false
            && validationInfo?.isValidating === false && !validationInfo.validationError) {
            props.loadWorkspaces(connectionId);
            setWorkspaceId(undefined);
            setsheetId(undefined);
        }
    }, [connectionId, connectionId && connections.connectionsVerification[connectionId]]);

    React.useEffect(() => {
        if (!isLinked && connectionId && workspaceId) {
            props.loadWorkspaceSheets(connectionId, workspaceId);
        }
    }, [connectionId, workspaceId, isLinked]);

    const connectionValidation = validateConnection(connections, connectionId);

    const sheetOptionsMap = React.useMemo<Dictionary<ISelectableOption[]>>(() =>
    ({
        [allWorkspacesKey]: sheetsToOptions(props.sheetsInfo.entities),
        ...sheetsMapToOptions(props.sheetsByWorkspaceMap)
    }), [props.sheetsInfo.entities, props.sheetsByWorkspaceMap]);
    const workspaceOptionsMap = React.useMemo(() => workspacesToOptions(props.workspacesInfo.entities), [props.workspacesInfo]);
    const isSheetAlreadyLinked = React.useMemo(() => {
        const sheet = (workspaceId && sheetsByWorkspaceMap.hasOwnProperty(workspaceId) ? sheetsByWorkspaceMap[workspaceId] : []).find(_ => _.sheetId === sheetId)
        return !allowMultipleConnectionsToOneProject && sheet?.isAlreadyLinked;
    }, [workspaceId, sheetId, sheetsByWorkspaceMap]);

    const onConnectionChange = React.useCallback((_: string | undefined) => {
        if (!isLinked) {
            setConnectionId(_);
            setWorkspaceId(undefined);
            setsheetId(undefined);
        }
    }, []);

    const onWorkspaceChange = React.useCallback((e: React.FormEvent<any>, _?: IComboBoxOption) => {
        const wsid=_ ? (_.key as number) : undefined;
        setWorkspaceId(wsid)
        setsheetId(undefined);
    }, [connectionId]);

    const isSheetSelectionDisabled = readonly || isLinked || !connectionId || sheetsInfo.isLoading || !workspaceId;
    const sheetOptions = workspaceId
        ? sheetOptionsMap[workspaceId]?.length
            ? sheetOptionsMap[workspaceId]
            : [{ key: "", text: "No items", disabled: true }]
        : [];

    const workspaceName = workspaceId ? (workspaceOptionsMap.find(_ => _.key === workspaceId)?.text ?? sourceInfo?.sourceData.workspaceName) : '--';
    const sheetName = sheetId ? (sheetOptions?.find(_ => _.key === sheetId)?.text ?? sourceInfo?.sourceData.sheetName) : '';

    return <>
        <div key="select-connection-container" className="with-top-margin">
            <SmartsheetConnectionSelect
                disabled={readonly || isLinked}
                connectionId={connectionId}
                onChange={onConnectionChange} />
        </div>
        <div key="select-project-container" className="with-top-margin">
            <OverlayComponent isOverlayed={!connectionId || connectionValidation?.isValidating !== false || !!connectionValidation?.validationError}>
                <ConnectionField
                    label="Workspace"
                    isLinked={isLinked}
                    isLoading={workspacesInfo.isLoading}
                    editor={<ComboBox
                        disabled={readonly || isLinked || !connectionId || workspacesInfo.isLoading}
                        allowFreeform
                        useComboBoxAsMenuWidth
                        placeholder="Select workspace"
                        selectedKey={workspaceId}
                        options={workspaceOptionsMap}
                        onChange={onWorkspaceChange}
                        errorMessage={!workspacesInfo.isLoading && connectionId && connectionValidation?.isValidating === false && !connectionValidation?.validationError
                            ? workspacesInfo.error ?? (workspaceId
                                ? undefined
                                : `Please select workspace from list`)
                            : undefined}
                    />}
                    viewer={<FieldValue value={workspaceName} />}
                />
                <div style={{ position: "relative" }}>
                    <ConnectionField
                        className="with-top-margin"
                        label="Sheet"
                        isLinked={isLinked}
                        isLoading={sheetsInfo.isLoading}
                        editor={<ComboBox
                            disabled={isSheetSelectionDisabled}
                            placeholder="Select sheet"
                            allowFreeform
                            useComboBoxAsMenuWidth
                            selectedKey={sheetId}
                            options={sheetOptions}
                            onRenderItem={onRenderItem}
                            onChange={(e, _) => setsheetId(_ ? (_.key as number) : undefined)}
                            errorMessage={isSheetSelectionDisabled
                                ? undefined
                                : sheetsInfo.error ?? (sheetId
                                    ? isSheetAlreadyLinked
                                        ? 'Sheet already linked'
                                        : undefined
                                    : `Please select sheet from list`)}
                        />}
                        viewer={<FieldValue value={sheetName} />}
                    />
                </div>
                {!disableConfigureConnection
                    && <div key="configure-connection-container" className="flex-space-between with-top-margin connection-configure-container">
                        <ActionButton 
                            disabled={!sheetId}
                            text="Configure mapping"
                            iconProps={{ iconName: "PPMXGear" }}
                            onClick={() => setIsConfigureConnection(true)} />
                    </div>
                }
            </OverlayComponent>
            {
                !disableConfigureConnection &&
                isConfigureConnection &&
                !!sheetId &&
                !!connectionId &&
                <SmartsheetMappingPanel
                    connectionId={connectionId}
                    itemSheetId={sheetId}
                    onDismiss={() => setIsConfigureConnection(false)} />
            }
            <OverlayComponent isOverlayed={!connectionId}>
                {isLinked
                    ? <PrimaryButton text="Delete sheet link"
                        className="with-top-margin"
                        disabled={readonly || sourceInfo?.syncStatus.inProgress}
                        title={sourceInfo?.syncStatus.inProgress ? "Sync in progress." : undefined}
                        onClick={() => deleteLink(props, connectionId!)} />
                    : <PrimaryButton text={`Link sheet`}
                        disabled={readonly || isSheetAlreadyLinked || workspaceId === undefined || sheetId === undefined}
                        className="with-top-margin"
                        onClick={() => {
                            if (showSwitchPrimaryDialog) {
                                setShowSwitchPrimaryScheduleConfirm(true);
                                return;
                            }
                            linkToProject(props, connectionId!, workspaceId!, sheetId!, props.setAsPrimary);
                        }} />
                }
            </OverlayComponent>
            {showSwitchPrimaryScheduleConfirm && <SwitchPrimaryScheduleConfirmation
                from={SourceType.Ppmx} to={SourceType.Smartsheet}
                onDismiss={() => setShowSwitchPrimaryScheduleConfirm(false)}
                onClick={(isPrimary) => linkToProject(props, connectionId!, workspaceId!, sheetId!, isPrimary)} />
            }
        </div>
    </>;
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        workspacesInfo: state.smartsheet.workspacesInfo,
        sheetsInfo: state.smartsheet.sheets,
        sheetsByWorkspaceMap: state.smartsheet.sheetsByWorkspaceMap,
        connections: state.smartsheet.connections
    };
}

export default connect(mapStateToProps, actionCreators)(SmartsheetConnectControl);